import React from "react";
import {
  Table,
  TableHeader,
  TableBody,
  RowWrapper,
  TableVariant,
  wrappable,
} from "@patternfly/react-table";
import {
  editableTableBody,
  editableRowWrapper,
  inlineEditFormatterFactory,
  TableEditConfirmation,
  TableTextInput,
} from "@patternfly/react-inline-edit-extension";
import dateFormatConverter, { stringToTimeStamp } from "../../utils/format";
import _orderBy from "lodash/orderBy";

export default class EditableTable extends React.Component {
  constructor(props) {
    super(props);

    const isLayeredProduct = props.product.is_layered_product;
    const showLastMinorRelease = props.product.show_last_minor_release;

    this.makeId = ({ rowIndex, columnIndex, name }) =>
      `${rowIndex}-${columnIndex}-${name ? `-${name}` : ""}`;

    // text input
    const inlineEditingFormatter = inlineEditFormatterFactory({
      renderEdit: (
        value,
        { columnIndex, rowIndex, column },
        { activeEditId }
      ) => {
        const id = this.makeId({ rowIndex, columnIndex });
        return (
          <TableTextInput
            id={id}
            defaultValue={value ? value.toString() : null}
            onBlur={(newValue) =>
              this.onBlur(newValue, {
                rowIndex,
                columnIndex,
              })
            }
            autoFocus={activeEditId === id}
          />
        );
      },
      renderValue: (value, { rowData }) =>
        rowData.isTableEditing ? `${value} (Not Editable)` : value,
      //isEditable: ({ rowIndex }) => rowIndex !== 1
    });

    const headers = this.props.productPhases.map((phase) => ({
      title: phase.name,
      cellFormatters: [inlineEditingFormatter],
      transforms: [wrappable],
    }));
    if (!!isLayeredProduct && props.product.product_extra_headers) {
      // headers.unshift({
      //   title: props.product.extra_header,
      //   cellFormatters: [inlineEditingFormatter],
      //   transforms: [wrappable],
      // });
      let product_extra_headers = _orderBy(
        props.product.product_extra_headers,
        ["id"],
        ["desc"]
      );
      product_extra_headers.forEach((extra_header) => {
        headers.unshift({
          title: extra_header.header,
          cellFormatters: [inlineEditingFormatter],
          transforms: [wrappable],
        });
      });
    }
    headers.unshift({
      title: "Weight",
      cellFormatters: [inlineEditingFormatter],
      transforms: [wrappable],
    });
    headers.unshift({
      title: "Version",
      cellFormatters: [inlineEditingFormatter],
      transforms: [wrappable],
    });

    if (!!showLastMinorRelease) {
      headers.push({
        title: "Last Minor Release",
        cellFormatters: [inlineEditingFormatter],
        transforms: [wrappable],
      });
    }

    const versionRows = this.props.product.versions.map((version) => {
      let cells = version.phases
        ? this.props.productPhases.map((productPhase) => {
            // if comment has value, return comment value, otherwise return enddate
            let matchedPhase = version.phases.find(
              (phase) => phase.name === productPhase.name
            );
            return dateFormatConverter(matchedPhase, true);
          })
        : this.props.productPhases.map((productPhase) => "N/A");

      // add last minor release if it is enabled
      if (!!showLastMinorRelease) {
        cells.push(
          version.last_minor_release ? version.last_minor_release : "N/A"
        );
      }

      // add base product version if it is layered product
      if (!!isLayeredProduct) {
        // cells.unshift(
        //   version.extra_header_value ? version.extra_header_value : "N/A"
        // );
        if (version.extra_header_values) {
          let extra_header_values = _orderBy(
            version.extra_header_values,
            ["header_id"],
            ["desc"]
          );
          extra_header_values.forEach((extra_value) => {
            cells.unshift(
              extra_value.extra_value ? extra_value.extra_value : "N/A"
            );
          });
        } else {
          props.product.product_extra_headers.forEach((extra_header) => {
            cells.unshift("N/A");
          });
        }
      }

      cells.unshift(version.weight.toString());
      cells.unshift(version.name);
      return { cells };
    });

    this.state = {
      columns: headers,
      rows:
        (this.props &&
          this.props.product &&
          this.props.product.versions &&
          JSON.parse(JSON.stringify(versionRows))) ||
        null,
      rowsBackup: null,
      activeEditId: this.props.activeEditId ? this.props.activeEditId : null,
      editVersions: [],
    };

    this.onBlur = (value, { rowIndex, columnIndex }) => {
      let versionWeightShift = 2; // version and weight take up to 2 columns
      let layeredProductShift = !!isLayeredProduct
        ? props.product.product_extra_headers.length
        : 0; // isLayeredProduct takes 1 column
      let minorReleaseShift = !!showLastMinorRelease ? 1 : 0; // showLastMinorRelease takes 1 column

      this.setState(({ rows, editVersions }) => {
        rows = [...rows];
        editVersions = [...editVersions];
        const row = rows[rowIndex];
        row.cells[columnIndex] = value;

        // start to compose editVersions
        // row is an array of [id, name, weight, phaseList[0], phaseList[1], phaseList[2] ... ]
        const originVersion = this.props.product.versions[rowIndex];
        const editVersionIndex = editVersions.findIndex(
          (version) => version.id === originVersion.id
        );
        const updatedVersion =
          editVersionIndex === -1
            ? { id: originVersion.id, phases: [] }
            : editVersions[editVersionIndex];

        if (!!isLayeredProduct && editVersionIndex === -1) {
          if (!originVersion.extra_header_values) {
            updatedVersion.extra_header_values = props.product.product_extra_headers.map(
              (header) => {
                return {
                  header_id: header.id,
                  extra_header: header.header,
                  extra_value: "",
                };
              }
            );
          } else {
            updatedVersion.extra_header_values = [];
          }
        }

        if (columnIndex === 0) {
          if (originVersion.name !== row.cells[0]) {
            updatedVersion.name = value;
          } else if (
            editVersionIndex !== -1 &&
            editVersions[editVersionIndex].name !== row.cells[0]
          ) {
            updatedVersion.name = value;
          }
        } else if (columnIndex === 1) {
          if (!originVersion.weight) {
            updatedVersion.weight = value;
          } else if (
            originVersion.weight &&
            originVersion.weight.toString() !== row.cells[1]
          ) {
            updatedVersion.weight = value === "" ? null : value;
          } else if (
            editVersionIndex !== -1 &&
            editVersions[editVersionIndex].weight !== row.cells[1]
          ) {
            updatedVersion.weight = value === "" ? null : value;
          }
        } else if (
          !!isLayeredProduct &&
          columnIndex >= 2 &&
          columnIndex <= props.product.product_extra_headers.length + 1
        ) {
          let extraIndex = columnIndex - versionWeightShift;
          if (originVersion.extra_header_values) {
            let matchedIndex = updatedVersion.extra_header_values.findIndex(
              (header) =>
                header.id === originVersion.extra_header_values[extraIndex].id
            );
            if (
              originVersion.extra_header_values[extraIndex].extra_value !==
              row.cells[columnIndex]
            ) {
              if (matchedIndex === -1) {
                let extra_value = {
                  id: originVersion.extra_header_values[extraIndex].id,
                  header_id:
                    originVersion.extra_header_values[extraIndex].header_id,
                  extra_header:
                    originVersion.extra_header_values[extraIndex].extra_header,
                  extra_value: value === "N/A" ? null : value,
                };
                updatedVersion.extra_header_values.push(extra_value);
              } else {
                updatedVersion.extra_header_values[matchedIndex].extra_value =
                  value === "" ? null : value;
              }
            } else if (
              editVersionIndex !== -1 &&
              editVersions[editVersionIndex].extra_header_values[
                matchedIndex
              ] &&
              editVersions[editVersionIndex].extra_header_values[matchedIndex]
                .extra_value !== row.cells[columnIndex]
            ) {
              updatedVersion.extra_header_values[matchedIndex].extra_value =
                value === "" ? null : value;
            }
          } else {
            if (
              updatedVersion.extra_header_values[extraIndex] &&
              updatedVersion.extra_header_values[extraIndex].extra_value !==
                row.cells[columnIndex]
            ) {
              updatedVersion.extra_header_values[extraIndex].extra_value =
                value === "" ? null : value;
            }
          }
        } else if (
          !!showLastMinorRelease &&
          columnIndex === row.cells.length - minorReleaseShift
        ) {
          if (!originVersion.last_minor_release) {
            updatedVersion.last_minor_release = value === "N/A" ? null : value;
          } else if (
            originVersion.last_minor_release &&
            originVersion.last_minor_release !== row.cells[row.cells.length - 1]
          ) {
            updatedVersion.last_minor_release = value === "" ? null : value;
          } else if (
            editVersionIndex !== -1 &&
            editVersions[editVersionIndex].last_minor_release !==
              row.cells[row.cells.length - 1]
          ) {
            updatedVersion.last_minor_release = value === "" ? null : value;
          }
        } else {
          let phaseIndex =
            columnIndex - versionWeightShift - layeredProductShift;
          const ts = stringToTimeStamp(value);
          let updatedPhase = {
            id: this.props.productPhases[phaseIndex].id,
            version_phases: {
              startdate: null,
              enddate: ts.date,
              comment: ts.string,
            },
          };
          updatedVersion.phases.push(updatedPhase);
        }
        if (editVersionIndex === -1) {
          editVersions.push(updatedVersion);
        } else {
          editVersions[editVersionIndex] = updatedVersion;
        }
        return {
          rows,
          editVersions,
          activeEditId: null, // stop autoFocus
        };
      });
    };

    this.onEditCellClicked = (
      event,
      clickedRow,
      { rowIndex, columnIndex, elementId }
    ) => {
      if (elementId !== this.state.activeEditId) {
        this.setState({
          activeEditId: elementId,
        });
      }
    };

    this.setEditing = (rows, isEditing) =>
      rows.map((row) => {
        row.isEditing = isEditing;
        return row;
      });

    this.onRowClick = () => {
      this.setState(
        ({ rows, rowsBackup }) =>
          !rowsBackup && {
            rowsBackup: JSON.parse(JSON.stringify(rows)), // clone
            rows: this.setEditing(rows, true),
          }
      );
    };

    this.onEditConfirmed = () => {
      this.setState(
        ({ rows }) => ({
          rows: this.setEditing(rows, false),
          rowsBackup: null,
          activeEditId: null,
        }),
        () => this.props.onConfirm(this.state.editVersions)
      );
    };

    this.onEditCanceled = () => {
      this.setState(({ rows, rowsBackup }) => ({
        rows: rowsBackup,
        rowsBackup: null,
        activeEditId: null,
      }));
      this.props.onCancelAddVersion();
      this.props.onCancel();
    };
  }

  render() {
    const { columns, rows, activeEditId } = this.state;
    const editConfig = {
      activeEditId,
      editConfirmationType: TableEditConfirmation.TABLE_BOTTOM,
      onEditCellClicked: this.onEditCellClicked,
      onEditConfirmed: this.onEditConfirmed,
      onEditCanceled: this.onEditCanceled,
    };

    const ComposedBody = editableTableBody(TableBody);
    const ComposedRowWrapper = editableRowWrapper(RowWrapper);

    return (
      <Table
        aria-label={`product-edit-table-${this.props.product.name}`}
        variant={TableVariant.compact}
        cells={columns}
        rows={rows}
        rowWrapper={ComposedRowWrapper}
        onCollapse={this.onCollapse}
      >
        <TableHeader />
        <ComposedBody
          rowKey="cells"
          editConfig={editConfig}
          onRowClick={this.onRowClick}
        />
      </Table>
    );
  }
}
